import React, { useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { CSVLink } from 'react-csv'
import { Button, BUTTON_SIZE, BUTTON_THEME, Icon } from '@st-operations/uikit/src/v2/index.js'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'

import convertObjAttrsSnakeToCamelCase from '_lib/convertObjAttrsSnakeToCamelCase'
import {
  deleteLookalikeAudienceSeed,
  cloneLookalikeAudienceSeeds,
  updateLookalikeAudienceSeeds,
  postCustomAudience,
} from '_services/audience'

import Header from '_components/header/index.jsx'
import CreateUserIDSeedsModal from '_components/create-user-id-seeds-modal'
import UserIDSeedsListView from './UserIDSeedsListView'

import Events from '@st-operations/uikit/Events.js'
import BatchSidebar, { ACTIONS } from '_components/batch-sidebar'

import './styles.scss'

export const createAudienceFromProcessedSeed = async (seed) => {
  return await postCustomAudience({
    name: `ShareThis > Interest from Social Activity > ${seed.name}`,
    destinations: [
      {
        _id: '632dec466420cb001a723e60',
        name: 'LiveRamp US 2',
        slug: 'liveramp-us-2',
      },
    ],
    userIdSeed: seed,
  })
}

const UserIDSeeds = () => {
  const [csvData, setCsv] = useState([])
  const [batchState, setBatchState] = useState({
    batch_percentage: 0,
    batch_size: 0,
    remountKey: 0,
  })
  const listViewItemsRef = useRef(null)

  const [modalOpen, setModalOpen] = useState(false)

  const history = useHistory()

  let dataToDownload = csvData?.map((content) => {
    const {
      name,
      status,
      createdAt,
      createdBy,
      updatedAt,
      updatedBy,
    } = convertObjAttrsSnakeToCamelCase(content)
    return {
      name,
      status,
      createdAt,
      createdBy: createdBy.email,
      updatedAt,
      updatedBy: updatedBy.email,
    }
  })
  const dataToDownloadOrdered = dataToDownload

  let headers = [
    { label: 'name', key: 'name' },
    { label: 'status', key: 'status' },
    { label: 'created_at', key: 'createdAt' },
    { label: 'create_by.email', key: 'createBy.email' },
    { label: 'updated_at', key: 'updatedAt' },
    { label: 'updated_by.email', key: 'updatedBy.email' },
  ]

  const csvReport = {
    filename: 'user-id-seeds.csv',
    headers,
    data: dataToDownloadOrdered ? dataToDownloadOrdered : [],
  }

  const handleGetItems = (items) => {
    setCsv(items)
  }

  const handleSelectionChange = ({ action, items }) => {
    const selectedEntities = listViewItemsRef.current.filter(({ _id }) => items.includes(_id))
    Events.emit('ListView-selection', { action, items, entities: selectedEntities })
  }

  const handleBatchSidebarAction = (action, items) => {
    switch (action) {
      case ACTIONS.ARCHIVE:
        return archive(items)
      case ACTIONS.CLONE:
        return clone(items)
      case ACTIONS.DELETE:
        return deleteSeeds(items)
    }
  }

  const startBatchProcess = (size) => {
    Events.emit('set_batch_process', true)
    setBatchState({ batch_percentage: 0, batch_size: size })
  }

  const updateBatchProgress = () => {
    setBatchState((prevState) => ({
      ...prevState,
      batch_percentage: prevState.batch_percentage + 1 / prevState.batch_size,
    }))
  }

  const softRefresh = (error) => !error && setBatchState({ remountKey: Math.random() })

  const archive = async (items) => {
    try {
      startBatchProcess(items.length)

      for (const item of items) {
        const payload = {
          status: 'archived',
        }
        await updateLookalikeAudienceSeeds(item._id, payload)
        updateBatchProgress()
      }

      softRefresh()
      toast.success('Seeds archived successfully')
    } catch (error) {
      toast.error('Error archiving seeds')
    }
  }

  const clone = async (items) => {
    try {
      startBatchProcess(items.length)

      for (const item of items) {
        const payload = {
          ...item,
          status: 'draft',
        }
        await cloneLookalikeAudienceSeeds(payload)
        updateBatchProgress()
      }

      softRefresh()
      toast.success('Seeds cloned successfully')
    } catch (error) {
      toast.error('Error cloning seeds')
    }
  }

  const deleteSeeds = async (items) => {
    try {
      startBatchProcess(items.length)

      for (const item of items) {
        await deleteLookalikeAudienceSeed(item._id)
        updateBatchProgress()
      }

      softRefresh()
      toast.success('Seeds deleted successfully')
    } catch (error) {
      toast.error('Error deleting seeds')
    }
  }

  return (
    <div
      id="lookalike-audience-seeds-page"
      className="lookalike-audience-seeds"
      key={batchState.remountKey}
    >
      <Header title="User ID Seeds">
        <CSVLink {...csvReport}>
          <Button theme={BUTTON_THEME.SECONDARY}>Download CSV</Button>
        </CSVLink>
        <Button
          theme={BUTTON_THEME.PRIMARY}
          onClick={() => setModalOpen((modalOpen) => setModalOpen(!modalOpen))}
        >
          Create New
        </Button>
      </Header>
      <div className="batch-sidebar__list-view-wrapper">
        <UserIDSeedsListView
          onGetItems={handleGetItems}
          renderItemActions={(item) => (
            <If condition={item.status === 'processed'}>
              <Button
                className="flex items-center justify-center gap-2 w-full text-md text-[12px]"
                theme={BUTTON_THEME.SECONDARY}
                size={BUTTON_SIZE.SMALLEST}
                onClick={async () => {
                  const { _id } = await createAudienceFromProcessedSeed(item)
                  history.push(`/audience-creator/${_id}`)
                }}
              >
                Create an Audience
                <Icon width="16px" height="16px" icon="externallink" type="outline" />
              </Button>
            </If>
          )}
          onRowClick={(item) => history.push(`/lookalike-audience-seeds/${item._id}`)}
          // sidebar actions
          showCheckboxes={true}
          onSelectionChange={handleSelectionChange}
          batch_percentage={batchState.batch_percentage}
          batch_size={batchState.batch_size}
          itemsRef={listViewItemsRef}
        />
        <BatchSidebar
          onConfirm={handleBatchSidebarAction}
          actions={[ACTIONS.ARCHIVE, ACTIONS.CLONE, ACTIONS.DELETE]}
        />
      </div>
      <Dialog open={modalOpen} close={() => setModalOpen(false)}>
        <CreateUserIDSeedsModal onClose={() => setModalOpen(false)} />
      </Dialog>
    </div>
  )
}

// export
export default UserIDSeeds
